import React, { useEffect } from "react";
import { Button, Checkbox, Loader, TextInput } from "@breward/ui";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiClient } from "@brewardapp/api-client";
import { useStore } from "@breward/store";
import { getSafeRedirectPath, redirectToParam } from "../../pages/LoginPage/redirect";

interface SharedLoginFormProps {
    redirectPath: string;
}

const SharedLoginForm: React.FC<SharedLoginFormProps> = ({
    redirectPath,
}) => {
    const setAuthToken = useStore(state => state?.setAuthToken)
    const setUserId = useStore(state => state?.setUserId)
    const showNotification = useStore(state => state?.showNotification)
    const setBackToUrl = useStore(state => state?.setBackToUrl)
    const backToUrl = useStore(state => state?.backToUrl)

    const api = useApiClient()
    const { register, handleSubmit } = useForm();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [t] = useTranslation();
    const redirectFromQuery = getSafeRedirectPath(searchParams.get(redirectToParam));

    const login = api.auth.useLogin()
    const onSubmit = (values) => {
        login.mutate(values, {
            onSuccess: (data) => {
                showNotification({
                    type: "success",
                    title: t("Notifications:userLoggedIn.title", "Logged in"),
                    message: t("Notifications:userLoggedIn.message", "Welcome in Breward."),
                });
                const redirectUrl = redirectFromQuery || (backToUrl && backToUrl !== "/" ? backToUrl : undefined);

                if (redirectUrl) {
                    setAuthToken?.(data?.accessToken, values?.rememberMe);
                    setBackToUrl?.(undefined);
                    window.location.href = redirectUrl;
                } else {
                    setAuthToken?.(data?.accessToken, values?.rememberMe);
                    navigate(redirectPath);
                }
            },
            onError: (error) => {
                showNotification({
                    type: "error",
                    title: error?.errors?.[0]?.title,
                    message: error?.errors?.[0]?.detail,
                });
            },
        });
    };

    useEffect(() => {
        const impersonate = searchParams.get("impersonate") === "true";
        const id = searchParams.get("id");
        const token = searchParams.get("token");

        if (impersonate && id && token) {
            setAuthToken?.(token);
            setUserId?.(id);
            navigate(redirectPath);
        }
    }, [searchParams, setAuthToken, setUserId, navigate, redirectPath]);


    return (
        <div className="">
            {login.isLoading ? (
                <Loader />
            ) : (
                <>
                    <TextInput
                        containerClassName="mb-2"
                        type="email"
                        autoComplete="email"
                        label={t("global:shared.emailLabel", "Email")}
                        {...register("email")}
                    />
                    <TextInput
                        containerClassName="mb-2"
                        autoComplete="password"
                        label={t("global:shared.passwordLabel", "Password")}
                        type="password"
                        {...register("password")}
                    />
                    <div className="mt-4">
                        <Checkbox
                            id="login-remember-me"
                            data-test-id="login-remember-me"
                            label={t("global:shared.rememberMe", "Keep me signed in")}
                            {...register("rememberMe")}
                        />
                    </div>
                    <Button
                        data-test-id="login-button"
                        onClick={() => handleSubmit(onSubmit)()}
                        className="mt-6 text-center text-lg uppercase w-full"
                    >
                        {t("global:shared.login", "Log In")}
                    </Button>
                </>
            )}
        </div>
    );
};

export default SharedLoginForm;
